/**
 * 画面上部 / モーダル内に出す「エラー」「警告」のバナー。
 *
 * 従来の `.error-message` は色付きの小さな文字だけで、背景も枠も無く見落としやすかった。
 * このバナーは背景色 + 枠 + アイコンで目に留まるようにする。
 * 既存の `.error-message` / `.success-message` は他画面が使っているので変更しない。
 *
 * - `error`: `role="alert"` (読み上げで即時に伝える)
 * - `warning`: `role="status"`
 * - `onDismiss` を渡すと「×」で閉じられる (エラーは自動で消さない)。
 */
import type React from 'react';

export interface StatusBannerProps {
  variant: 'error' | 'warning';
  /** 本文 */
  children: React.ReactNode;
  /** 指定すると「×」ボタンを表示し、押下時に呼ぶ */
  onDismiss?: () => void;
  'data-testid'?: string;
}

const ICONS: Record<StatusBannerProps['variant'], string> = {
  error: '⚠',
  warning: '⚠',
};

export function StatusBanner(props: StatusBannerProps): React.JSX.Element {
  const { variant, children, onDismiss } = props;
  return (
    <div
      className={`status-banner status-banner--${variant}`}
      role={variant === 'error' ? 'alert' : 'status'}
      data-testid={props['data-testid']}
    >
      <span className="status-banner__icon" aria-hidden="true">
        {ICONS[variant]}
      </span>
      <div className="status-banner__body">{children}</div>
      {onDismiss ? (
        <button
          type="button"
          className="status-banner__close"
          aria-label="メッセージを閉じる"
          onClick={onDismiss}
        >
          ×
        </button>
      ) : null}
    </div>
  );
}
